import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { red } from '@mui/material/colors';
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import inventoryExceptionsService from "@plannel/services/rp/inventory-exceptions-service";

import SplitterContainer from "@plannel/components/SplitterContainer";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";

import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import CodeUtil from "@plannel/pages/utils/code-util";

const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode } = CodeUtil;
const { SIMULATION, PLANNED, RELEASED } = versionStatusCode;
const InventoryExceptions = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const pageMoveFilterRef = useRef({});
  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [invExceptions, setInvExceptions] = useState([]);
  const [totalRowData, setTotalRowData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const gridRef = useRef();
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 상세 화면이 권한 밖이면 링크 표시(파란 밑줄)도 클릭도 없앤다.
  const canOpenDetail = isPermitted("INV_EXC_DETAIL");

  const remainingCellStyle = params => {
    const { value, data } = params;
    if (canOpenDetail && value > 0 && data?.baseDate) {
      return { color: 'blue', textDecoration: 'underline', cursor: 'pointer' };
    }

    return {};
  }

  const onRemainingClicked = params => {
    const { data, value } = params;

    if (canOpenDetail && value > 0 && data?.baseDate) {
      const reduxKey = "INV_EXC_DETAIL";
      const reduxState = reduxUtil.createObjectState(reduxKey, { 
        itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current,
        baseDate: data.baseDate,
        ...pageMoveFilterRef.current
      });

      reduxDispatch(reduxUtil.updateViewState(reduxState));
      reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    }
  }

  const columnDefs = [
    { headerName: "", field: "displayHeader", type: ["nonEditableColumn"], flex: 1, pinned: "left", lockPinned: true },
    { headerName: "totalExceptions", field: "totalExceptions", type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 2 },
    { headerName: "exceptionsComplete", field: "exceptionsComplete", type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 2 },
    {
      headerName: "exceptionsRemaining", field: "exceptionsRemaining", type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 2,
      cellStyle: remainingCellStyle, onCellClicked: onRemainingClicked
    },
    { headerName: "complete", field: "completeRatio", type: ["percentColumn", "nonEditableColumn", "rightAligned"], flex: 2 }
  ];

  const defaultTypeOptions = { stackId: "exception", strokeWidth: 1.5, barSize: 40 };
  const chartSeries = [
    {
      id: "exceptionsRemaining",
      field: "exceptionsRemaining",
      text: t("exceptionsRemaining"),
      type: "bar",
      typeOptions: { ...defaultTypeOptions, fill: red[300] },
      groupName: t("total")
    },
    {
      id: "exceptionsComplete",
      field: "exceptionsComplete",
      text: t("exceptionsComplete"),
      type: "bar",
      typeOptions: { ...defaultTypeOptions, fill: ColorUtil.colorSet(0) },
      groupName: t("total")
    }
  ];

  const getRequestParams = () => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
      params.bucket = rpVersionFilter.planConfigObj?.BUCKET_UNIT;
    }
    
    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    return params;
  }

  const getTotalRow = results => {
    const totalRow = { totalExceptions: 0, exceptionsComplete: 0, exceptionsRemaining: 0, completeRatio: 0 };
    for (const result of results) {
      totalRow.totalExceptions += result.totalExceptions;
      totalRow.exceptionsComplete += result.exceptionsComplete;
    }
    totalRow.exceptionsRemaining = totalRow.totalExceptions - totalRow.exceptionsComplete;
    totalRow.completeRatio = totalRow.totalExceptions > 0 ? totalRow.exceptionsComplete / totalRow.totalExceptions : 0;

    setTotalRowData(totalRow);
  }

  const retrieveInvExceptions = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    inventoryExceptionsService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setInvExceptions(results);
        getTotalRow(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, filter: filterValue, rpVersion: rpVersionFilter });
        reduxDispatch(reduxUtil.updateViewState(pageState));

        gridRef.current?.api?.setGridOption('loading', false);
      })
  }

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  /*
   * 대시보드 드릴스루는 redux 에 `{ id }` 스텁만 심는다(InventoryDashboard / LocationInventoryDashboard).
   * RpVersionFilter 가 id 로 전체 버전 객체를 찾아 onChangeFilter 로 교체해 주므로,
   * 그 전(=statusCd 가 없는 스텁)에는 조회하지 않는다. 스텁으로 조회하면 planConfigObj 가 없어 터진다.
   */
  const isVersionResolved = !isEmpty(rpVersionFilter?.statusCd);

  useEffect(() => {
    if (isVersionResolved && isLoading) {
      pageMoveFilterRef.current = { rpVersion: rpVersionFilter, filter: filterValue };
      retrieveInvExceptions();
    } else {
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter, filterValue, isLoading]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[SIMULATION, PLANNED, RELEASED]} />
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <RPItemAutocomplete retrieve={retrieveInvExceptions} sx={{ mr: 1 }} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
      </FilterContainer>      <SplitterContainer initialSizes={[1, 1]} collapseDirection='up'>
        <Box className="chart-area" sx={{ height: "100%" }}>
          <ChartComposed
            xAxis={"displayHeader"}
            dataSeries={chartSeries}
            data={invExceptions}
            options={{ margin: { left: 10, top: 20, right: 20, bottom: 30 } }}
            tooltipAnimation={false}
          />
        </Box>
        <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 22px)", mt: 2 }}>
          <AgGridReact
            ref={gridRef}
            rowData={invExceptions}
            {...defaultGridMemo}
            pinnedBottomRowData={[totalRowData]}
            onGridReady={onGridReady}
          />
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(InventoryExceptions);